import React, { useState, useEffect, useMemo } from 'react';
import { useProducts } from './useProducts';
import { useParams } from 'react-router-dom';
import ImageModal from './ImageModal';
import VideoModal from './VideoModal';
import {
  productHasVideo,
  primaryVideoSource,
  videoPosterSrc,
  hrefForSameSiteAsset,
  productGalleryImagePaths,
} from './productMediaUtils';
import { formatDisplayTitle, formatProductTitle } from './formatProductTitle';
import AddToCartButton from './cart/AddToCartButton';
import ProductLetterRibbon from './cart/ProductLetterRibbon';
import WishlistHeartButton from './wishlist/WishlistHeartButton';
import ProductPrice from './catalog/ProductPrice';
import ProductRefId from './catalog/ProductRefId';
import { Link } from 'react-router-dom';
import { productDetailPath } from './productPaths';
import ProductListingToolbar, { filterInStockOnly, sortProducts } from './catalog/ProductListingToolbar';
import CatalogGridSkeleton from './catalog/CatalogGridSkeleton';
import CatalogPagination from './catalog/CatalogPagination';

const RAKHI_SLUG = 'rakhi';

/** Match legacy /shop/rakhi/:type URLs: group key is product type OR subcategory (same as LumbaList + catalog tiles). */
function productMatchesRakhiGroup(product, groupKey) {
  if (!groupKey) return true;
  const g = String(groupKey).trim().toLowerCase();
  const typ = String(product.type || '')
    .trim()
    .toLowerCase();
  const sub = String(product.subcategory || '')
    .trim()
    .toLowerCase();
  return typ === g || sub === g;
}

const rakhiTypes = [
  { key: 'single', label: 'Single Rakhi' },
  { key: 'band', label: 'Band Rakhi' },
  { key: 'bhaiya-bhabi', label: 'Bhaiya Bhabi Rakhi' },
  { key: 'bracelet-set', label: 'Bracelet Rakhi Set' },
  { key: 'personalized', label: 'Personalized Name Bracelets' },
  { key: 'lumba', label: 'Lumbas' },
  { key: 'kids', label: 'Kids Rakhi' },
  { key: 'girls-lumba', label: 'Girls Lumba Rakhi' },
  { key: 'newborn', label: 'Newborn Rakhi' },
  { key: 'pet', label: 'Pet Rakhi' },
];

const RakhiList = () => {
  const { type } = useParams();
  const decodedType = type ? decodeURIComponent(type) : '';
  const { allProducts, categories, loading } = useProducts();
  const rakhiProducts = allProducts.filter((p) => p.category === RAKHI_SLUG);
  const rakhiMeta = useMemo(() => categories.find((c) => c.slug === RAKHI_SLUG), [categories]);

  const sectionTitle = useMemo(() => {
    if (!decodedType) return '';
    const legacy = rakhiTypes.find((t) => t.key === decodedType);
    if (legacy) return legacy.label;
    const fromSub = rakhiMeta?.subcategories?.find((s) => s.slug === decodedType);
    if (fromSub) return fromSub.name || formatDisplayTitle(fromSub.slug.replace(/-/g, ' '));
    const fromTyp = rakhiMeta?.types?.find((t) => t.slug === decodedType);
    if (fromTyp) return fromTyp.name || formatDisplayTitle(fromTyp.slug.replace(/-/g, ' '));
    return formatDisplayTitle(decodedType.replace(/-/g, ' '));
  }, [decodedType, rakhiMeta]);

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('default');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [imageModal, setImageModal] = useState({ show: false, image: '', alt: '' });
  const [videoModal, setVideoModal] = useState({ show: false, video: '', alt: '', noSound: true });
  const [currentImageIndex, setCurrentImageIndex] = useState({}); // Track current image for each product
  const [currentProduct, setCurrentProduct] = useState(null); // Track the current product being viewed
  const pageSize = 8;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [decodedType]);

  useEffect(() => {
    setPage(1);
  }, [decodedType]);

  const productsToShow = useMemo(() => {
    let list = decodedType
      ? rakhiProducts.filter((p) => productMatchesRakhiGroup(p, decodedType))
      : rakhiProducts;
    list = filterInStockOnly(list, inStockOnly);
    return sortProducts(list, sort);
  }, [decodedType, rakhiProducts, inStockOnly, sort]);

  const totalPages = Math.ceil(productsToShow.length / pageSize) || 1;
  const paginated = productsToShow.slice((page - 1) * pageSize, page * pageSize);

  // Function to handle page changes and scroll to top
  const handlePageChange = (newPage) => {
    setPage(newPage);
    // Scroll to top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to get current image for a product
  const getCurrentImage = (product) => {
    const paths = productGalleryImagePaths(product);
    if (paths.length === 0) return '';
    const index = currentImageIndex[product.id] || 0;
    return paths[Math.min(Math.max(0, index), paths.length - 1)] || paths[0];
  };

  // Function to cycle through images
  const handleImageNavigation = (product, direction) => {
    if (!product) return;
    const paths = productGalleryImagePaths(product);
    if (paths.length <= 1) return;
    const currentIndex = currentImageIndex[product.id] || 0;
    let newIndex;

    if (direction === 'next') {
      newIndex = (currentIndex + 1) % paths.length;
    } else {
      newIndex = currentIndex === 0 ? paths.length - 1 : currentIndex - 1;
    }

    setCurrentImageIndex({
      ...currentImageIndex,
      [product.id]: newIndex,
    });

    if (imageModal.show && currentProduct?.id === product.id) {
      setImageModal({
        ...imageModal,
        image: paths[newIndex],
        alt: formatProductTitle(product.name),
      });
    }
  };

  const handleMediaClick = (product) => {
    setCurrentProduct(product);
    const isVideo = productHasVideo(product);
    const src = primaryVideoSource(product);

    if (isVideo && src) {
      setVideoModal({
        show: true,
        video: src,
        alt: formatProductTitle(product.name),
        noSound: product.noSound !== false,
      });
    } else {
      const paths = productGalleryImagePaths(product);
      if (paths.length === 0) return;
      const index = Math.min(Math.max(0, currentImageIndex[product.id] || 0), paths.length - 1);
      setImageModal({
        show: true,
        image: paths[index],
        alt: formatProductTitle(product.name),
      });
    }
  };

  return (
    <div className="RakhiList-main product-list-page">
      <ImageModal 
        show={imageModal.show}
        image={imageModal.image}
        alt={imageModal.alt}
        onClose={() => {
          setImageModal({ show: false, image: '', alt: '' });
          setCurrentProduct(null);
        }}
        onNext={() => currentProduct && handleImageNavigation(currentProduct, 'next')}
        onPrev={() => currentProduct && handleImageNavigation(currentProduct, 'prev')}
        hasMultipleImages={currentProduct ? productGalleryImagePaths(currentProduct).length > 1 : false}
        currentIndex={currentProduct ? currentImageIndex[currentProduct.id] || 0 : 0}
        totalImages={currentProduct ? productGalleryImagePaths(currentProduct).length : 1}
      />
      <VideoModal 
        show={videoModal.show} 
        video={videoModal.video} 
        alt={videoModal.alt} 
        noSound={videoModal.noSound}
        onClose={() => setVideoModal({ show: false, video: '', alt: '', noSound: true })} 
      />
      
      {decodedType ? (
        <div>
          {decodedType === 'personalized' ? (
            <div className="personalized-hero">
              <div className="container">
                <h1 className="personalized-title">Personalized Name Bracelets & Rakhi</h1>
                <p className="personalized-subtitle">Make it uniquely yours</p>
                <p className="personalized-description">
                  Create a one-of-a-kind piece with custom names, initials, or special messages. 
                  Our artisans carefully craft each personalized rakhi and bracelet to celebrate your unique bond.
                </p>
              </div>
            </div>
          ) : (
            <h2 className="page-heading-script mb-4 text-center">
              {formatDisplayTitle(sectionTitle)}
            </h2>
          )}
          
          <ProductListingToolbar
            sort={sort}
            onSortChange={setSort}
            inStockOnly={inStockOnly}
            onInStockOnlyChange={setInStockOnly}
            count={productsToShow.length}
            className="mb-3"
          />

          {loading ? (
            <CatalogGridSkeleton count={4} />
          ) : paginated.length === 0 ? (
            <div className="text-center py-5">
              <h3>No products found in this collection</h3>
              <p>Please check back later for new arrivals.</p>
            </div>
          ) : (
            <div className="product-grid">
              {paginated.map(product => {
              const isVideo = productHasVideo(product);
              const videoSrc = primaryVideoSource(product);
              const galleryPaths = productGalleryImagePaths(product);
              const hasMultipleImages = galleryPaths.length > 1;
              const currentIndex = currentImageIndex[product.id] || 0;
              const totalImages = galleryPaths.length || 1;
              
              return (
                <div key={product.id} className="product-card">
                  <div className="product-media-container" onClick={() => handleMediaClick(product)}>
                    <ProductLetterRibbon product={product} />
                    <WishlistHeartButton
                      product={product}
                      categorySlug={RAKHI_SLUG}
                      size="sm"
                      className="product-card__wishlist"
                    />
                    {isVideo && videoSrc ? (
                      <div className="video-thumbnail">
                        <img src={videoPosterSrc(product)} alt={formatProductTitle(product.name)} />
                        <div className="play-icon">▶</div>
                      </div>
                    ) : (
                      <div className="image-container">
                        <img 
                          src={hrefForSameSiteAsset(getCurrentImage(product) || '')} 
                          alt={formatProductTitle(product.name)} 
                          loading="lazy"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          style={{
                            width: '100%',
                            height: 'auto',
                            aspectRatio: '1',
                            objectFit: 'cover',
                          }}
                        />
                        {hasMultipleImages && (
                          <div className="image-navigation" onClick={e => e.stopPropagation()}>
                            <button 
                              onClick={() => handleImageNavigation(product, 'prev')}
                              className="nav-btn prev"
                              aria-label="Previous image"
                            >
                              ❮
                            </button>
                            <div className="image-counter">{currentIndex + 1}/{totalImages}</div>
                            <button 
                              onClick={() => handleImageNavigation(product, 'next')}
                              className="nav-btn next"
                              aria-label="Next image"
                            >
                              ❯
                            </button>
                          </div>
                        )}
                        {product.type === 'personalized' && (
                          <div className="personalized-badge">Personalized</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="product-info">
                    <h3 className="product-title">
                      <Link
                        to={productDetailPath({ ...product, category: RAKHI_SLUG })}
                        className="text-decoration-none text-body"
                      >
                        {formatProductTitle(product.name)}
                      </Link>
                    </h3>
                    <ProductRefId id={product.id} />
                    <ProductPrice product={product} className="product-price" showBadge={false} />
                    <AddToCartButton product={product} categorySlug={RAKHI_SLUG} className="mt-2" compact />
                  </div>
                </div>
              );
            })}
            </div>
          )}
          
          {totalPages > 1 && (
            <CatalogPagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
          )}
        </div>
      ) : (
        <div className="text-center py-5">Please select a collection</div>
      )}
    </div>
  );
};

export default RakhiList;
